import React from "react";

/**
 * Shared fleet filter UI used by ProfilePage, FleetPage, and TeamPage.
 *
 * Renders a status segmented control (All / Pledged / In-Game) and a search
 * input on a single row. Parents own the filter state — this is a controlled
 * component so each page can wire it to its own URL hash / persist preference
 * if it wants.
 *
 * Pair with `applyFleetFilter` below to filter ship entries consistently.
 *
 * Props:
 *   value: { status: 'all'|'pledged'|'in_game', search: string }
 *   onChange(next)
 *   accent?: color string (defaults to the gold profile accent)
 *   placeholder?: text for the search input
 *   totalCount?: optional count shown to the right (e.g. "12 ships")
 */
const ACCENT_DEFAULT = "#ce93d8";

export const SHIP_STATUS = {
  PLEDGED: "pledged",
  IN_GAME: "in_game",
};

export const SHIP_STATUS_LABELS = {
  pledged: "Pledged",
  in_game: "In-Game",
};

// Treat any ship entry without an explicit status as 'pledged' — that's the
// most common SC fleet entry type and matches the pre-existing data.
export function getShipStatus(entry) {
  const s = entry?.status;
  if (s === SHIP_STATUS.IN_GAME) return SHIP_STATUS.IN_GAME;
  return SHIP_STATUS.PLEDGED;
}

/**
 * Filter an array of ship entries by the current filter value.
 *
 * @param {Array} entries — [{ ship_id, quantity, primary, status, ... }]
 * @param {Object} filter — { status: 'all'|'pledged'|'in_game', search: string }
 * @param {Function} getShipInfo — (ship_id) => { name, manufacturer, ... } | null
 * @returns filtered array
 */
export function applyFleetFilter(entries, filter, getShipInfo) {
  if (!Array.isArray(entries)) return [];
  const status = filter?.status || "all";
  const q = (filter?.search || "").trim().toLowerCase();
  return entries.filter(entry => {
    if (status !== "all" && getShipStatus(entry) !== status) return false;
    if (q) {
      const ship = getShipInfo ? getShipInfo(entry.ship_id) : null;
      const hay = `${ship?.name || ""} ${ship?.manufacturer || ""} ${ship?.category || ""} ${ship?.family || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export default function FleetFilterBar({ value, onChange, accent = ACCENT_DEFAULT, placeholder = "Search fleet…", totalCount = null }) {
  const status = value?.status || "all";
  const search = value?.search || "";

  const setStatus = (next) => onChange?.({ status: next, search });
  const setSearch = (next) => onChange?.({ status, search: next });

  const STATUSES = [
    { key: "all", label: "All" },
    { key: SHIP_STATUS.PLEDGED, label: SHIP_STATUS_LABELS.pledged },
    { key: SHIP_STATUS.IN_GAME, label: SHIP_STATUS_LABELS.in_game },
  ];

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
      padding: "10px 12px",
      border: "1px solid rgba(255,255,255,0.06)",
      background: "rgba(255,255,255,0.02)",
      marginBottom: 16,
    }}>
      {/* Status segmented control */}
      <div style={{ display: "flex", border: "1px solid rgba(255,255,255,0.08)" }}>
        {STATUSES.map(s => {
          const active = status === s.key;
          return (
            <button key={s.key} type="button" onClick={() => setStatus(s.key)}
              style={{
                padding: "6px 12px",
                fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase",
                background: active ? `${accent}18` : "transparent",
                border: "none",
                color: active ? accent : "rgba(232,234,246,0.5)",
                cursor: "pointer",
                borderRight: "1px solid rgba(255,255,255,0.06)",
              }}>
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div style={{ flex: "1 1 200px", display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", background: "rgba(0,0,0,0.25)", border: "1px solid rgba(255,255,255,0.06)" }}>
        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(232,234,246,0.3)" }}>⌕</span>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={placeholder}
          style={{
            flex: 1, background: "transparent", border: "none", outline: "none",
            fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "#e8eaf6", padding: "4px 0",
          }}
        />
        {search && (
          <button type="button" onClick={() => setSearch("")}
            style={{ background: "none", border: "none", color: "rgba(232,234,246,0.4)", cursor: "pointer", fontSize: 12, padding: 0, lineHeight: 1 }}>
            ✕
          </button>
        )}
      </div>

      {totalCount != null && (
        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.4)", textTransform: "uppercase" }}>
          {totalCount} shown
        </span>
      )}
    </div>
  );
}
